"use client";

import { useMemo } from "react";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { toAuthenticatedUser } from "@/lib/utils/crm/user-adapters";
import type { AuthenticatedUser } from "@/lib/types/unified";

type UniversalAuth = ReturnType<typeof useUniversalAuth>;

/**
 * `useUniversalAuth()` with its `appUser` adapted to the UI `AuthenticatedUser`
 * shape — and the result MEMOIZED.
 *
 * WHY THIS EXISTS (do not "simplify" it back to a bare call):
 * `toAuthenticatedUser` SPREADS its input into a NEW object, so calling it
 * inline during render returns a different reference every render even when the
 * auth context has not changed. `appUser` is a member-level dependency of two
 * downstream context memos — the SimpleTable `stable` slice and the
 * `UniversalEntityPage` `auth` slice (dep #1 in `useCompositionStableSlices`) —
 * so one fresh reference per render silently defeated both memos, and with them
 * the `React.memo` on every virtualized table row and cell. See
 * `docs/engineering-rules/MEMO_STABLE_PROPS.md`.
 *
 * That fix used to be five hand-copied `useMemo` lines at five call sites; this
 * hook is the single seam so a sixth caller cannot reintroduce the bug.
 *
 * Returns the FULL auth context (callers variously need `isClientUser`,
 * `isLoading`, `isEmployee`, `isAdmin`, `isAuthenticated`, `isInitialized`)
 * with `appUser` narrowed to `AuthenticatedUser | null`. The returned object is
 * itself memoized on the (already memoized) context value, so destructuring it
 * or keying a dep array on it are both stable.
 */
export type UseAuthenticatedUserReturn = Omit<UniversalAuth, "appUser"> & {
  appUser: AuthenticatedUser | null;
};

export function useAuthenticatedUser(): UseAuthenticatedUserReturn {
  const auth = useUniversalAuth();
  const rawAppUser = auth.appUser;
  // `rawAppUser` is `useState`-held in the auth provider, so keying on it is stable.
  const appUser = useMemo(() => toAuthenticatedUser(rawAppUser), [rawAppUser]);

  return useMemo(() => ({ ...auth, appUser }), [auth, appUser]);
}
